import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useSelector } from 'react-redux';
import http from '../api';
import { toast } from 'react-toastify';

const SalesChart = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7); // 7 o 30 días
  const [chartType, setChartType] = useState('line'); // line o bar
  const { user } = useSelector((state) => state.auth);
  const { tenant } = useSelector((state) => state.tenant);
  
  // Obtener tenantId del usuario o del tenant
  const tenantId = user?.tenantId?._id || user?.tenantId || tenant?._id || tenant?.id;

  useEffect(() => {
    if (tenantId) {
      fetchSalesData();
    }
    // eslint-disable-next-line
  }, [period, tenantId]);

  const fetchSalesData = async () => {
    try {
      // If tenantId is missing, skip fetching and show empty series
      if (!tenantId) {
        setSalesData(generateEmptyData(period));
        setLoading(false);
        return;
      }
      setLoading(true);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - period);

      // Add a timeout to avoid hanging the UI if backend is unreachable
      const reqPromise = http.get('/reports/sales-by-date', {
        params: {
          tenantId: tenantId,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      const timeoutMs = 8000;
      const timeoutPromise = new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), timeoutMs));
      const response = await Promise.race([reqPromise, timeoutPromise]);

      // Formatear datos para el gráfico (usar los totales desagregados por estado)
      const raw = Array.isArray(response.data) ? response.data : [];
      // Parsear la clave de fecha del backend (YYYY-MM-DD) como fecha UTC
      const formatted = raw.map(item => {
        const parts = String(item._id).split('-').map(p => Number(p));
        const utcDate = new Date(Date.UTC(parts[0], (parts[1] || 1) - 1, parts[2] || 1));
        return {
          date: utcDate.toLocaleDateString('es-ES', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
          ventasCompleted: Number(item.totalCompleted || 0),
          ventasPending: Number(item.totalPending || 0),
          transacciones: Number(item.count || 0)
        };
      });

      // Si la API responde con arreglo vacío, mostrar serie con ceros
      if (!formatted || formatted.length === 0) {
        setSalesData(generateEmptyData(period));
      } else {
        setSalesData(formatted);
      }
    } catch (error) {
      console.error('Error al cargar datos de ventas:', error);
      // Use a shared toastId to avoid duplicate toasts from multiple report components
      toast.error('Error al cargar estadísticas de ventas', { toastId: 'reports-error' });
      // Datos de ejemplo en caso de error
      setSalesData(generateMockData(period));
    } finally {
      setLoading(false);
    }
  };

  const generateMockData = (days) => {
    const data = [];
    for (let i = days - 1; i >= 0; i--) {
      const now = new Date();
      // Usar fechas basadas en UTC para mantener coherencia con el backend
      const utcBase = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const d = new Date(utcBase);
      d.setUTCDate(utcBase.getUTCDate() - i);
      data.push({
        date: d.toLocaleDateString('es-ES', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
        ventasCompleted: Math.floor(Math.random() * 3000) + 500,
        ventasPending: Math.floor(Math.random() * 2000),
        transacciones: Math.floor(Math.random() * 50) + 10
      });
    }
    return data;
  };
  
  const generateEmptyData = (days) => {
    const data = [];
    for (let i = days - 1; i >= 0; i--) {
      const now = new Date();
      const utcBase = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const d = new Date(utcBase);
      d.setUTCDate(utcBase.getUTCDate() - i);
      data.push({
        date: d.toLocaleDateString('es-ES', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
        ventasCompleted: 0,
        ventasPending: 0,
        transacciones: 0
      });
    }
    return data;
  };

  const formatCurrency = (value) => {
    try {
      const n = Number(value || 0);
      // Mostrar con símbolo $ en vez de ₡, mantener separación de miles
      return `$${n.toLocaleString('es-ES', { minimumFractionDigits: 0 })}`;
    } catch (e) {
      return `$0`;
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>📊 Ventas por Día</h3>
        <div style={styles.controls}>
          <select 
            value={period} 
            onChange={(e) => setPeriod(Number(e.target.value))}
            style={styles.select}
          >
            <option value={7}>Últimos 7 días</option>
            <option value={15}>Últimos 15 días</option>
            <option value={30}>Últimos 30 días</option>
          </select>
          <select 
            value={chartType} 
            onChange={(e) => setChartType(e.target.value)}
            style={styles.select}
          >
            <option value="line">Línea</option>
            <option value="bar">Barras</option>
          </select>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        {chartType === 'line' ? (
          <LineChart data={salesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" tickFormatter={formatCurrency} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => v} />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'ventasCompleted') return [formatCurrency(value), 'Ventas completadas'];
                    if (name === 'ventasPending') return [formatCurrency(value), 'Ventas pendientes'];
                    return [value, name === 'transacciones' ? 'Transacciones' : name];
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="ventasCompleted" 
                  stroke="#0088FE" 
                  strokeWidth={2}
                  name="Ventas completadas"
                  yAxisId="left"
                />
                <Line 
                  type="monotone" 
                  dataKey="ventasPending" 
                  stroke="#FF8042" 
                  strokeWidth={2}
                  name="Ventas pendientes"
                  yAxisId="left"
                />
                <Line 
                  type="monotone" 
                  dataKey="transacciones" 
                  stroke="#82ca9d" 
                  strokeWidth={2}
                  name="Transacciones"
                  yAxisId="right"
                />
          </LineChart>
        ) : (
          <BarChart data={salesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" tickFormatter={formatCurrency} />
            <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => v} />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'ventasCompleted') return [formatCurrency(value), 'Ventas completadas'];
                if (name === 'ventasPending') return [formatCurrency(value), 'Ventas pendientes'];
                return [value, name === 'transacciones' ? 'Transacciones' : name];
              }}
            />
            <Legend />
            <Bar dataKey="ventasCompleted" fill="#0088FE" name="Ventas completadas" yAxisId="left" />
            <Bar dataKey="ventasPending" fill="#FF8042" name="Ventas pendientes" yAxisId="left" />
            <Bar dataKey="transacciones" fill="#82ca9d" name="Transacciones" yAxisId="right" />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '20px'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px'
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    color: '#333'
  },
  controls: {
    display: 'flex',
    gap: '10px'
  },
  select: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px'
  }
};

export default SalesChart;
